// src/Redux/MenuCategory/menuCategoryApi.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import { MenuItem } from '../Menu/menuSlice';
import { createApiRequest } from '../../utils/apiUtils';

export interface MenuCategory {
  id: string;
  name: string;
  sortIndex: number;
  items: MenuItem[];
}

/**
 * カテゴリ＋紐付けメニュー一覧を取得
 */
export const fetchMenuCategories = createAsyncThunk<MenuCategory[], string>(
  'menuCategory/fetchMenuCategories',
  async (storeId, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuCategory[]>(`/stores/${storeId}/categories`, {
        method: 'GET',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * カテゴリ追加
 */
export const addMenuCategory = createAsyncThunk<
  MenuCategory,
  { storeId: string; name: string }
>(
  'menuCategory/addMenuCategory',
  async ({ storeId, name }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuCategory>(`/stores/${storeId}/categories`, {
        method: 'POST',
        requiresAuth: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, sortIndex: 0 }),
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * カテゴリ削除
 */
export const deleteMenuCategory = createAsyncThunk<
  void,
  { storeId: string; categoryId: string }
>(
  'menuCategory/deleteMenuCategory',
  async ({ storeId, categoryId }, { rejectWithValue }) => {
    try {
      await createApiRequest(`/stores/${storeId}/categories/${categoryId}`, {
        method: 'DELETE',
        requiresAuth: true,
      });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * メニューをカテゴリに紐付け
 */
export const attachMenuToCategory = createAsyncThunk<
  MenuCategory,
  { storeId: string; menuCategoryId: string; menuId: string; sortIndex: number }
>(
  'menuCategory/attachMenuToCategory',
  async ({ storeId, menuCategoryId, menuId, sortIndex }, { rejectWithValue }) => {
    try {
      return await createApiRequest<MenuCategory>(
        `/stores/${storeId}/categories/${menuCategoryId}/menus/${menuId}/attach`,
        {
          method: 'POST',
          requiresAuth: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sortIndex }),
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * メニューとカテゴリの紐付けを解除
 */
export const detachMenuFromCategory = createAsyncThunk<
  void,
  { storeId: string; menuCategoryId: string; menuId: string }
>(
  'menuCategory/detachMenuFromCategory',
  async ({ storeId, menuCategoryId, menuId }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/categories/${menuCategoryId}/menus/${menuId}/detach`,
        {
          method: 'DELETE',
          requiresAuth: true,
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * カテゴリの並び順を更新
 */
export const updateMenuCategorySortIndex = createAsyncThunk<
  void,
  { storeId: string; categoryId: string; sortIndex: number }
>(
  'menuCategory/updateMenuCategorySortIndex',
  async ({ storeId, categoryId, sortIndex }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/categories/${categoryId}/sort-index`,
        {
          method: 'PUT',
          requiresAuth: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sortIndex }),
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * カテゴリの順序を再配置
 */
export const reorderMenuCategories = createAsyncThunk<
  void,
  { storeId: string; reorderedCategories: MenuCategory[] }
>(
  'menuCategory/reorderMenuCategories',
  async ({ storeId, reorderedCategories }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/categories/reorder`,
        {
          method: 'PUT',
          requiresAuth: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ categories: reorderedCategories }),
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * カテゴリ内のメニューの順序を再配置
 */
export const reorderMenusInCategory = createAsyncThunk<
  void,
  { storeId: string; categoryId: string; menus: Array<{ menuId: string; sortIndex: number }> }
>(
  'menuCategory/reorderMenusInCategory',
  async ({ storeId, categoryId, menus }, { rejectWithValue }) => {
    try {
      await createApiRequest(
        `/stores/${storeId}/categories/${categoryId}/menus/reorder`,
        {
          method: 'PUT',
          requiresAuth: true,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ menus }),
        }
      );
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);